import { useMemo } from 'react';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import type { LocalShareAction } from '#src/app/shared/components/localSignerShare';
import { useCurrentUser } from '#src/app/shared/hooks/session';
import { getLocationLabel } from '#src/app/shared/utils/location';

import { useShareActions } from '../../ShareActions/hooks/useShareActions';
import type { ShareActionItem } from '../../ShareActions/hooks/useShareActions/mapButtonConfigs';

type Result = Readonly<{
	/** Real share configs (copy + channels) flattened for the local-signer screens. */
	shareActions: readonly LocalShareAction[];
	/** The petition's relevant place (city or state, by reach), else undefined (component fallback). */
	location: string | undefined;
	/** The signer's first name for the goal greeting, when identified. */
	userName: string | undefined;
	isFirstLocalSigner: boolean;
	/** Count of *other* local signers ("You joined N other local signers"). */
	otherSignerCount: number;
}>;

/** Derives the real data the local-signer share/goal screens need from the share page + session. */
export function useConstituentShareData(): Result {
	const {
		data: { petition },
	} = usePsfSharePageData();
	const currentUser = useCurrentUser();

	const { data: shareActionsData } = useShareActions();
	const shareActions = useMemo<readonly LocalShareAction[]>(
		() => [shareActionsData.first, ...shareActionsData.rest].filter((a): a is ShareActionItem => Boolean(a)),
		[shareActionsData.first, shareActionsData.rest],
	);

	// The screens refer to the decision maker generically as "[location] leaders" (CHANGE-101758). The
	// relevant location follows the petition's reach ("Nashville, TN" or "Tennessee, US"), and its leading
	// segment names that place, so a state petition shows the state, not a city.
	const location = useMemo(
		() => getLocationLabel(petition.relevantLocationLocalizedName),
		[petition.relevantLocationLocalizedName],
	);

	// `constituentCountAmongSigners` counts the constituents among the petition's signers (the just-signed
	// signer included), so "other local signers" is one fewer. A missing/zero count degrades to the
	// first-signer copy.
	const rawCount = petition.decisionMakersConnection.edges.at(0)?.associationData?.constituentCountAmongSigners ?? 0;

	return {
		shareActions,
		location,
		userName: currentUser?.firstName,
		isFirstLocalSigner: rawCount <= 1,
		otherSignerCount: Math.max(rawCount - 1, 0),
	};
}
